'use client';

/**
 * The owner's view of an agent's channel relay (webagents gap-closure plan
 * item 2.2, 2026-09-26): the channels connected to the agent, who may reach
 * it over them (the sender policy and the per-sender rate), the senders
 * waiting for approval with their pairing codes, the senders approved or
 * blocked with what each has used, an allowlist entry, and approval by code.
 * Mounted in the Channels section of the agent settings (`agent-details.tsx`);
 * the data comes from `app/api/agents/[id]/channels`.
 *
 * WHAT A PERSON SEES (the portal visual pass, 2026-09-28, fixed by the
 * final-portal-ui lane):
 *   - an error sits next to the form or row that failed and is announced
 *     (`role="alert"`). The pairing-code error used to render at the top of
 *     the panel, about 320 px above its input and out of view;
 *   - channels by name ("Telegram", "SMS", "Google Chat"), never the relay's
 *     slug, and a sender as its channel plus its id on it, never the
 *     `channel:telegram:<id>` principal;
 *   - no "Last seen" for a sender who never wrote (an allowlisted one);
 *   - every control has a label tied to it by id, and its hint by
 *     `aria-describedby`.
 * The server's error strings are English and name internals, so a refusal is
 * mapped from its status to a translated sentence here.
 */

import { useCallback, useEffect, useId, useState } from 'react';
import { Ban, Check, Clock, RefreshCw, Trash2, UserPlus } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { ProviderIcon } from '@/components/provider-icon';
import { Spinner } from '@/components/ui/spinner';

interface Sender {
  id: string;
  channelType: string;
  senderId: string;
  principal: string;
  displayName: string | null;
  status: 'pending' | 'approved' | 'blocked';
  pairingCode: string | null;
  chatId: string | null;
  messageCount: number;
  heldCount: number;
  turnCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

interface Policy {
  senderPolicy: 'open' | 'pairing' | null;
  perMinute: number;
  perHour: number;
  effectiveSenderPolicy: 'open' | 'pairing';
}

interface Connection {
  provider: string;
  type: string | null;
  label: string;
}

interface Props {
  agentId: string;
}

function formatDate(raw: string | null) {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * A channel's name as a person reads it. The relay's vocabulary is a slug
 * (`CHANNEL_TYPE_BY_PROVIDER` in `lib/messaging/channel-identity.ts`); a slug
 * added there before its key exists here reads as the slug, never as a raw
 * translation key.
 */
export function channelName(t: Translate, type: string | null | undefined): string {
  if (!type) return '';
  const key = `agentChannels.channelNames.${type}`;
  const name = t(key);
  return name && name !== key ? name : type;
}

/**
 * The line under a sender's name: the channel, and the sender's id on it when
 * the name above is a display name (otherwise the name above IS the id).
 */
export function senderLine(t: Translate, sender: Pick<Sender, 'channelType' | 'senderId' | 'displayName'>): string {
  const channel = channelName(t, sender.channelType);
  return sender.displayName ? `${channel} · ${sender.senderId}` : channel;
}

/** Why approving by code was refused, from the route's status (`approveChannelSenderByCode`). */
export function codeErrorMessage(t: Translate, status: number): string {
  if (status === 400) return t('agentChannels.codeNotRecognised');
  if (status === 404) return t('agentChannels.codeNotFound');
  if (status === 409) return t('agentChannels.codeExpired');
  return t('agentChannels.saveFailed');
}

/** Why an allowlist entry was refused, from the route's status (`allowChannelSender`). */
export function allowErrorMessage(t: Translate, status: number): string {
  return status === 400 ? t('agentChannels.allowInvalidSender') : t('agentChannels.saveFailed');
}

export function AgentChannelsPanel({ agentId }: Props) {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [senderPolicy, setSenderPolicy] = useState<'open' | 'pairing'>('pairing');
  const [perMinute, setPerMinute] = useState('');
  const [perHour, setPerHour] = useState('');
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [code, setCode] = useState('');
  const [allowType, setAllowType] = useState('telegram');
  const [allowSender, setAllowSender] = useState('');
  const [allowName, setAllowName] = useState('');
  const [busy, setBusy] = useState(false);
  // Each failure is shown where it happened: `error` is the panel's own
  // (loading, the policy form), the others sit under their form or row.
  const [codeError, setCodeError] = useState<string | null>(null);
  const [allowError, setAllowError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const ids = {
    policy: useId(),
    policyHint: useId(),
    perMinute: useId(),
    perHour: useId(),
    limitHint: useId(),
    code: useId(),
    codeError: useId(),
    allowType: useId(),
    allowSender: useId(),
    allowName: useId(),
    allowHint: useId(),
    allowError: useId(),
  };

  const base = `/api/agents/${encodeURIComponent(agentId)}/channels`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(base);
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { policy: Policy; senders: Sender[]; connections: Connection[]; types: string[] };
      setSenders(data.senders ?? []);
      setPolicy(data.policy);
      setConnections(data.connections ?? []);
      setTypes(data.types ?? []);
      setSenderPolicy(data.policy.effectiveSenderPolicy);
      setPerMinute(String(data.policy.perMinute));
      setPerHour(String(data.policy.perHour));
      if (data.types?.length && !data.types.includes('telegram')) setAllowType(data.types[0]);
    } catch {
      setError(t('agentChannels.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [base, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const savePolicy = async () => {
    setSavingPolicy(true);
    setError(null);
    try {
      const res = await fetch(base, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderPolicy,
          perMinute: perMinute.trim() === '' ? null : Number(perMinute),
          perHour: perHour.trim() === '' ? null : Number(perHour),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { policy: Policy };
      setPolicy(data.policy);
      setPerMinute(String(data.policy.perMinute));
      setPerHour(String(data.policy.perHour));
    } catch {
      setError(t('agentChannels.saveFailed'));
    } finally {
      setSavingPolicy(false);
    }
  };

  const decide = async (sender: Sender, status: 'approved' | 'blocked') => {
    setBusy(true);
    setRowError(null);
    try {
      const res = await fetch(`${base}/${encodeURIComponent(sender.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(String(res.status));
      await load();
    } catch {
      setRowError({ id: sender.id, message: t('agentChannels.saveFailed') });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sender: Sender) => {
    if (!window.confirm(t('agentChannels.removeConfirm', { name: sender.displayName || sender.senderId }))) return;
    setBusy(true);
    setRowError(null);
    try {
      const res = await fetch(`${base}/${encodeURIComponent(sender.id)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(String(res.status));
      await load();
    } catch {
      setRowError({ id: sender.id, message: t('agentChannels.saveFailed') });
    } finally {
      setBusy(false);
    }
  };

  const approveByCode = async () => {
    if (!code.trim()) return;
    setBusy(true);
    setCodeError(null);
    try {
      const res = await fetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!res.ok) {
        setCodeError(codeErrorMessage(t, res.status));
        return;
      }
      setCode('');
      await load();
    } catch {
      setCodeError(t('agentChannels.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const allow = async () => {
    if (!allowSender.trim()) return;
    setBusy(true);
    setAllowError(null);
    try {
      const res = await fetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: allowType, senderId: allowSender.trim(), displayName: allowName.trim() || undefined }),
      });
      if (!res.ok) {
        setAllowError(allowErrorMessage(t, res.status));
        return;
      }
      setAllowSender('');
      setAllowName('');
      await load();
    } catch {
      setAllowError(t('agentChannels.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const pending = senders.filter((s) => s.status === 'pending');
  const decided = senders.filter((s) => s.status !== 'pending');

  const senderRow = (sender: Sender) => (
    <div key={sender.id} className="rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-2">
          <ProviderIcon slug={sender.channelType === 'email' ? 'sendgrid' : sender.channelType === 'sms' ? 'twilio' : sender.channelType} size={18} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium truncate">{sender.displayName || sender.senderId}</span>
              <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{t(`agentChannels.status.${sender.status}`)}</span>
              {sender.pairingCode && (
                <span className="text-[10px] font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded">{t('agentChannels.code', { code: sender.pairingCode })}</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate" data-testid="channel-sender-line">{senderLine(t, sender)}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="channel-sender-usage">
              {/* A sender allowed in advance has a row but no message yet: its
                  `lastSeenAt` is the moment it was allowed, not a visit. */}
              {sender.messageCount === 0 ? (
                t('agentChannels.noMessagesYet')
              ) : (
                <>
                  {t('agentChannels.usage', { messages: sender.messageCount, turns: sender.turnCount })}
                  {sender.heldCount > 0 ? ` · ${t('agentChannels.held', { count: sender.heldCount })}` : ''}
                  {' · '}
                  {t('agentChannels.lastSeen', { when: formatDate(sender.lastSeenAt) })}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {sender.status !== 'approved' && (
            <button type="button" onClick={() => void decide(sender, 'approved')} disabled={busy} className="text-xs text-primary hover:bg-primary/10 px-2 py-1 rounded-md transition-colors flex items-center gap-1" title={sender.status === 'blocked' ? t('agentChannels.unblock') : t('agentChannels.approve')}>
              <Check className="h-3.5 w-3.5" /> {sender.status === 'blocked' ? t('agentChannels.unblock') : t('agentChannels.approve')}
            </button>
          )}
          {sender.status !== 'blocked' && (
            <button type="button" onClick={() => void decide(sender, 'blocked')} disabled={busy} className="text-xs text-destructive hover:bg-destructive/10 px-2 py-1 rounded-md transition-colors flex items-center gap-1" title={t('agentChannels.block')}>
              <Ban className="h-3.5 w-3.5" /> {t('agentChannels.block')}
            </button>
          )}
          <button type="button" onClick={() => void remove(sender)} disabled={busy} className="text-xs text-muted-foreground hover:bg-muted/50 p-1.5 rounded-md transition-colors" aria-label={t('agentChannels.remove')} title={t('agentChannels.remove')}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {rowError?.id === sender.id && (
        <p role="alert" className="text-xs text-destructive mt-2">{rowError.message}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{t('agentChannels.title')}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('agentChannels.subtitle')}</p>
        </div>
        <button type="button" onClick={() => void load()} className="text-xs text-muted-foreground hover:bg-muted/50 p-1.5 rounded-md transition-colors shrink-0" title={t('agentChannels.refresh')} aria-label={t('agentChannels.refresh')}>
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}

      {loading && !policy ? (
        <div className="flex items-center justify-center py-6">
          <Spinner size="lg" tone="muted" />
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('agentChannels.connectedTitle')}</p>
            {connections.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('agentChannels.noneConnected')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {connections.map((c, i) => {
                  // The channel by name, then the account it is bound to. The
                  // route falls back to the provider slug when the account has
                  // no handle, and that slug is not a name.
                  const name = channelName(t, c.type);
                  const account = c.label && c.label !== c.provider ? c.label : '';
                  const text = name ? (account ? `${name} · ${account}` : name) : account || c.provider;
                  return (
                    <span key={`${c.provider}-${i}`} className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs" data-testid="channel-connection">
                      <ProviderIcon slug={c.provider} size={14} /> {text}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="flex flex-col @md/main:flex-row @md/main:items-center gap-2">
              <label className="flex-1 text-sm font-medium" htmlFor={ids.policy}>{t('agentChannels.policyLabel')}</label>
              <select id={ids.policy} aria-describedby={ids.policyHint} value={senderPolicy} onChange={(e) => setSenderPolicy(e.target.value as 'open' | 'pairing')} className="input text-sm @md/main:max-w-[300px]">
                <option value="pairing">{t('agentChannels.policyPairing')}</option>
                <option value="open">{t('agentChannels.policyOpen')}</option>
              </select>
            </div>
            <p id={ids.policyHint} className="text-xs text-muted-foreground">{t('agentChannels.policyHint')}</p>
            <div className="flex flex-col @md/main:flex-row gap-2">
              <div className="flex-1 text-sm">
                <label htmlFor={ids.perMinute} className="text-xs text-muted-foreground block mb-1">{t('agentChannels.perMinute')}</label>
                <input id={ids.perMinute} aria-describedby={ids.limitHint} type="number" min={0} max={600} value={perMinute} onChange={(e) => setPerMinute(e.target.value)} className="input w-full text-sm" />
              </div>
              <div className="flex-1 text-sm">
                <label htmlFor={ids.perHour} className="text-xs text-muted-foreground block mb-1">{t('agentChannels.perHour')}</label>
                <input id={ids.perHour} aria-describedby={ids.limitHint} type="number" min={0} max={10000} value={perHour} onChange={(e) => setPerHour(e.target.value)} className="input w-full text-sm" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p id={ids.limitHint} className="text-xs text-muted-foreground">{t('agentChannels.limitHint')}</p>
              <button type="button" onClick={() => void savePolicy()} disabled={savingPolicy} className="btn-primary text-xs">
                {savingPolicy ? t('actions.saving') : t('actions.save')}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {t('agentChannels.pendingTitle')}
            </p>
            {pending.length === 0 ? <p className="text-sm text-muted-foreground">{t('agentChannels.nonePending')}</p> : pending.map(senderRow)}
            <div className="flex flex-col @md/main:flex-row gap-2">
              <input
                id={ids.code}
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (codeError) setCodeError(null);
                }}
                placeholder={t('agentChannels.approveByCodePlaceholder')}
                aria-label={t('agentChannels.approveByCodeLabel')}
                aria-invalid={codeError ? true : undefined}
                aria-describedby={codeError ? ids.codeError : undefined}
                className="input text-sm font-mono @md/main:max-w-[200px]"
              />
              <button type="button" onClick={() => void approveByCode()} disabled={busy || !code.trim()} className="btn-secondary text-xs flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5" /> {t('agentChannels.approveByCodeLabel')}
              </button>
            </div>
            {codeError && (
              <p id={ids.codeError} role="alert" className="text-xs text-destructive" data-testid="channel-code-error">{codeError}</p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('agentChannels.sendersTitle')}</p>
            {decided.length === 0 ? <p className="text-sm text-muted-foreground">{t('agentChannels.none')}</p> : decided.map(senderRow)}
          </div>

          <div className="rounded-lg border border-dashed border-border p-3 space-y-2">
            <p className="text-sm font-medium flex items-center gap-1.5">
              <UserPlus className="h-4 w-4 text-muted-foreground" /> {t('agentChannels.allowTitle')}
            </p>
            <div className="flex flex-col @md/main:flex-row gap-2">
              <select id={ids.allowType} value={allowType} onChange={(e) => setAllowType(e.target.value)} className="input text-sm @md/main:max-w-[160px]" aria-label={t('agentChannels.allowChannel')}>
                {types.map((type) => (
                  <option key={type} value={type}>{channelName(t, type)}</option>
                ))}
              </select>
              <input
                id={ids.allowSender}
                type="text"
                value={allowSender}
                onChange={(e) => {
                  setAllowSender(e.target.value);
                  if (allowError) setAllowError(null);
                }}
                placeholder={t('agentChannels.allowSender')}
                aria-label={t('agentChannels.allowSender')}
                aria-invalid={allowError ? true : undefined}
                aria-describedby={allowError ? `${ids.allowError} ${ids.allowHint}` : ids.allowHint}
                className="input flex-1 text-sm"
              />
              <input id={ids.allowName} type="text" value={allowName} onChange={(e) => setAllowName(e.target.value)} placeholder={t('agentChannels.allowName')} aria-label={t('agentChannels.allowName')} className="input flex-1 text-sm" />
              <button type="button" onClick={() => void allow()} disabled={busy || !allowSender.trim()} className="btn-secondary text-xs">
                {t('agentChannels.allow')}
              </button>
            </div>
            {allowError && (
              <p id={ids.allowError} role="alert" className="text-xs text-destructive" data-testid="channel-allow-error">{allowError}</p>
            )}
            <p id={ids.allowHint} className="text-xs text-muted-foreground">{t('agentChannels.allowSenderHint')}</p>
          </div>
        </>
      )}
    </div>
  );
}
